import { useState } from "react";
import { Link } from "react-router-dom";

import { useMarketItems, useScanOffers } from "../api/queries";
import type { OfferMode } from "../api/types";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { Variation } from "../components/Variation";
import { adena, ago, plural, signed, when } from "../format";
import { itemHref, traderHref } from "../search/nav";
import type { ModeRow, ModeSort } from "./marketSort";
import { delta, filterByItem, isStale, modeRows, sortModeRows } from "./marketSort";

const SORTS: ModeSort[] = ["seen", "item", "best", "traders"];
const SORT_LABELS: Record<ModeSort, string> = {
  seen: "last seen", item: "item", best: "best", traders: "traders",
};

/** Craft is the same row shape with different words over it: a crafter charging
 *  a fee is still one trader offering one thing at one price. Migration 003 is
 *  clear the fee is not an ask, which is why it gets no mode colour — rose and
 *  olive are WTS and WTB, and a fee is neither. */
const LABELS: Record<OfferMode, {
  title: string; who: string; counter: string; best: string; price: string; hue: string;
}> = {
  buy: { title: "Buy", who: "Trader", counter: "Traders", best: "Best buy", price: "Price", hue: "buy" },
  sell: { title: "Sell", who: "Trader", counter: "Traders", best: "Best sell", price: "Price", hue: "sell" },
  craft: { title: "Craft", who: "Crafter", counter: "Crafters", best: "Best fee", price: "Fee", hue: "" },
};

/** One mode across the whole history. The scan-scoped twin is Offers.tsx, at
 *  /scans/:uid/<mode>; the two differ in meaning, not just in which scan, which
 *  is why they are two components and not one with a flag. */
export function MarketOffers({ mode }: { mode: OfferMode }) {
  const [sort, setSort] = useState<ModeSort>("seen");
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<number | null>(null);

  const market = useMarketItems();
  const label = LABELS[mode];

  if (market.isError) {
    return (
      <>
        <TitleBand title={label.title} />
        <ErrorBand
          message="Could not load the market."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }
  if (!market.data) {
    return <><TitleBand title={label.title} /><Skeleton rows={8} /></>;
  }

  const { items, modes, truncated } = market.data;
  const all = modeRows(items, mode);
  const rows = sortModeRows(filterByItem(all, filter), sort, mode);
  const needle = filter.trim();
  const chosen = rows.find((row) => row.item_id === selected);
  const offers = rows.reduce((sum, row) => sum + row.quote.offers, 0);

  return (
    <>
      <TitleBand
        title={label.title}
        note={`${plural(rows.length, "item")} · ${plural(offers, "offer")}`}
      />
      <Panel primary>
        <div className="strip">
          <span>Sort</span>
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => setSort(option)}
            >
              {SORT_LABELS[option]}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <input
            aria-label="Filter items"
            placeholder="filter item…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {truncated ? (
          <p className="scan-meta">
            showing the most recently seen items — the history holds more
          </p>
        ) : null}

        {/* Three states, told apart. A mode nobody ever walked is not a market
            with nothing in it, and one message for both would have someone
            hunting for a market problem that is a scan setting. */}
        {rows.length === 0 ? (
          needle ? (
            <Empty>Nothing matching “{needle}”.</Empty>
          ) : modes.length === 0 ? (
            // `modes`, not `items.length`. An empty `items` means no item has a
            // priced sighting, not that nothing was ever scanned — a scan that
            // walked packages only leaves it empty — whereas `modes` is the
            // union of every mode any scan ever walked, so an empty one is the
            // only honest evidence that there are no scans at all.
            <Empty>
              No scans yet. Run <code>python -m market_analyzer scan</code> on the
              scanning machine, then <code>sync</code>.
            </Empty>
          ) : modes.includes(mode) ? (
            <Empty>No {mode} offers have ever been scanned.</Empty>
          ) : (
            <Empty>
              No scan has ever walked {mode}. Run a scan with {mode} enabled to
              see them here.
            </Empty>
          )
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">{label.best}</th>
                  <th className="num">{label.counter}</th>
                  <th className="num">vs last</th>
                  <th>Where</th>
                  <th className="num">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ModeTableRow
                    key={row.item_id}
                    row={row}
                    hue={label.hue}
                    selected={row.item_id === selected}
                    onSelect={() =>
                      setSelected(row.item_id === selected ? null : row.item_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {chosen ? <ChosenOffers row={chosen} mode={mode} label={label} /> : null}
    </>
  );
}

function ModeTableRow({ row, hue, selected, onSelect }: {
  row: ModeRow; hue: string; selected: boolean; onSelect: () => void;
}) {
  const { quote } = row;
  const move = delta(quote);

  return (
    <tr className={selected ? "sel" : undefined} onClick={onSelect}>
      <td>
        <Link to={itemHref(row.item_id, row.item)} onClick={(e) => e.stopPropagation()}>
          {row.item}
        </Link>
      </td>
      <td className={`num ${hue}`}>{adena(quote.best)}</td>
      <td className="num dim">{quote.traders}</td>
      <td
        className={`num ${move === null ? "dim" : ""}`}
        title="Against the previous scan that saw this item"
      >
        {signed(move)}
      </td>
      <td className="dim">{quote.city || "—"}</td>
      <td
        className={`num ${isStale(quote.observed_at) ? "dim" : ""}`}
        title={when(quote.observed_at)}
      >
        {ago(quote.observed_at)}
      </td>
    </tr>
  );
}

/** The chosen item's offers, from the one scan its number came from — so the
 *  traders listed add up to the `best` and the count in the row above. */
function ChosenOffers({ row, mode, label }: {
  row: ModeRow; mode: OfferMode; label: { who: string; price: string; hue: string };
}) {
  const offers = useScanOffers(row.quote.scan_uid, row.item);
  const rows = (offers.data?.offers ?? [])
    .filter((offer) => offer.mode === mode)
    .sort((a, b) => {
      if (a.price === null) return 1;
      if (b.price === null) return -1;
      return mode === "buy" ? b.price - a.price : a.price - b.price;
    });
  // The count is of the rows actually rendered, not the scan's own total for
  // this mode. They differ when the 200-row fetch — which spans every mode —
  // truncated before this mode's rows were exhausted. Quoting quote.offers
  // alone would claim more rows than sit underneath it.
  //
  // `rows.length` is 0 while the fetch is in flight and 0 when it failed, so the
  // "showing N of M" form is only true once the data is actually here. Until
  // then the scan's own count is the honest thing to print: it comes from the
  // aggregate, not from this request, so it is already known and already correct.
  const shown = offers.isSuccess && rows.length !== row.quote.offers
    ? `showing ${rows.length} of ${row.quote.offers} in that scan, best first`
    : `${row.quote.offers} in that scan, best first`;

  return (
    <>
      <TitleBand
        title={row.item}
        note={`${row.quote.city || "unknown town"}, ${ago(row.quote.observed_at)} · ${shown}`}
      />
      <Panel>
        {offers.isLoading ? (
          <Skeleton rows={3} />
        ) : offers.isError ? (
          // Without this the table renders its headers over zero rows, which
          // reads as "this item had no offers" — a state that cannot occur,
          // since the row above was derived from at least one.
          <ErrorBand
            message="Could not load that scan's offers."
            hint="The market itself loaded fine. Reload in a moment."
          />
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>{label.who}</th>
                  {mode === "craft" ? null : <th className="num">Qty</th>}
                  <th className="num">{label.price}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((offer) => (
                  <tr key={offer.id}>
                    <td>
                      <Link to={traderHref(offer.nick)}>{offer.nick}</Link>
                      {/* Beside the trader, as on the per-scan page: this panel
                          is already one item, and what separates two traders'
                          daggers is exactly this. */}
                      <Variation {...offer} />
                    </td>
                    {mode === "craft" ? null : <td className="num">{adena(offer.qty)}</td>}
                    <td className={`num ${label.hue}`}>{adena(offer.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
